import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '@/api/index';
import { BASE_URL, defaultHeaders, xApiKey } from '@/constants/index';

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Order'],
  endpoints: (builder) => ({
    getCustomerContract: builder.query({
      query: ({ lang }) => ({
        url: `/assets/esimData/contract-${lang}.json`,
      }),
    }),
    getDeviceList: builder.query({
      query: () => ({
        baseURL: BASE_URL,
        url: 'esim-sales/esim/common/devices',
        headers: {
          ...defaultHeaders,
          Referrer: 1,
        },
        method: 'GET',
      }),
    }),
    postSales: builder.mutation({
      query: ({ trackId, offerId }) => ({
        url: 'esim-sales/esim/sales',
        method: 'POST',
        data: {
          trackId,
          primaryOfferId: offerId,
        },
        headers: {
          ...defaultHeaders,
          'x-api-key': xApiKey,
        },
        baseURL: BASE_URL,
      }),
    }),
    swap: builder.mutation({
      query: ({ trackId }) => ({
        url: 'esim-sales/esim/swap',
        method: 'POST',
        params: {
          trackId,
        },
        headers: {
          ...defaultHeaders,
          Referrer: 1,
          'x-api-key': xApiKey,
        },
        baseURL: BASE_URL,
      }),

      invalidatesTags: (result, error, trackId) => [{ type: 'Swap', trackId }],
    }),
    checkCustomer: builder.mutation({
      query: ({ ...data }) => ({
        url: 'esim-sales/esim/customer/check',
        method: 'POST',
        data,
        headers: {
          ...defaultHeaders,
          'x-api-key': xApiKey,
        },
        baseURL: BASE_URL,
      }),
    }),
    verifyCustomer: builder.mutation({
      query: ({ ...data }) => ({
        url: 'esim-sales/esim/customer/verify',
        method: 'POST',
        data,
        headers: defaultHeaders,
        baseURL: BASE_URL,
      }),
    }),
    faceRecognition: builder.mutation({
      query: ({ ...data }) => {
        return {
          url: 'esim-sales/esim/customer/face-recognition',
          method: 'POST',
          data: data?.file,
          params: {
            trackId: data?.trackId,
          },
          headers: { ...defaultHeaders },
          baseURL: BASE_URL,
        };
      },
    }),
    getFaceRecognitionResult: builder.query({
      query: (trackId) => ({
        url: 'esim-sales/esim/customer/face-recognition',
        method: 'GET',
        headers: defaultHeaders,
        baseURL: BASE_URL,
        params: {
          trackId,
        },
      }),
    }),
    sima: builder.mutation({
      query: ({ trackId, formData }) => {
        return {
          url: 'esim-sales/esim/face-recognition/sima',
          method: 'POST',
          params: {
            trackId,
          },
          baseURL: BASE_URL,
          data: formData,
        };
      },
    }),
    getRecognitionRules: builder.query({
      query: (data) => ({
        url: 'face-recognition/rules',
        baseURL: BASE_URL,
        method: 'POST',
        data,
      }),
    }),
    paymentRequest: builder.mutation({
      query: ({ lang, amount, redirectUrl, receiver = null }) => {
        return {
          baseURL: BASE_URL,
          url: '/prod/bakcell-payment/pay/without-card',
          method: 'POST',
          headers: { 'Accept-Language': lang },
          data: {
            amount,
            redirectUrl,
          },
          params: {
            receiver,
            web: true,
          },
        };
      },
    }),
  }),
});

export const hooks = {
  useGetCustomerContract: orderApi.useGetCustomerContractQuery,
  usePostSales: orderApi.usePostSalesMutation,
  useSwap: orderApi.useSwapMutation,
  useCheckCustomer: orderApi.useCheckCustomerMutation,
  useVerifyCustomer: orderApi.useVerifyCustomerMutation,
  useGetDeviceList: orderApi.useGetDeviceListQuery,
  useFaceRecognitionMutation: orderApi.useFaceRecognitionMutation,
  useGetFaceRecognitionResult: orderApi.useGetFaceRecognitionResultQuery,
  useGetRecognitionRules: orderApi.useGetRecognitionRulesQuery,
  useSima: orderApi.useSimaMutation,
  usePaymentRequest: orderApi.usePaymentRequestMutation,
};

export default orderApi;
